import { api } from '@/services'
const qs = require('qs')
import Vue from 'vue'
import {
  getSignedUrl,
  uploadToS3,
  attachToResource,
} from '@/services/upload/uploadService'

const state = {
  adminAppointmentFiles: [],
  adminAppointmentOutputs: [],
  adminAppointmentFilesToDelete: [],
  adminAppointmentOutputsToDelete: [],
  isAppointmentOutputsUploading: false,
}

const getters = {
  adminAppointmentFiles: (state) => state.adminAppointmentFiles,
  adminAppointmentOutputs: (state) => state.adminAppointmentOutputs,
  adminAppointmentFilesToDelete: (state) => state.adminAppointmentFilesToDelete,
  adminAppointmentOutputsToDelete: (state) => state.adminAppointmentOutputsToDelete,
  isAppointmentOutputsUploading: (state) => state.isAppointmentOutputsUploading,
}

const actions = {
  async fetchAppointmentFilesAsAdmin({commit, dispatch }, appointmentId) {
    commit('setAppointmentFilesLoading', true)
    commit('setAppointmentId', appointmentId)

    const files = []
    const terms = []
    const termsSigned = []

    try {
      const { data } = await api.get(`/admin/appointmentFiles/${appointmentId}`)

      for await (const file of data.files) {
        if (file?.file_source === 'TERM') terms.push(file)
        else if (file?.file_source === 'SIGNED_TERM') termsSigned.push(file)
        else files.push(file)
      }

      commit('setAppointmentTerms', terms)
      commit('setAdminAppointmentFiles', files)
      commit('setAdminAppointmentOutputs', data.outputs)
      commit('setAppointmentTermsSigned', termsSigned)
    } catch ({ message }) {
      dispatch('showErrorMessage', message)
    } finally {
      commit('setAppointmentFilesLoading', false)
    }
  },

  async attachAppointmentOutputsAsAdmin({ commit, dispatch, rootState }, files) {
    commit('setIsAppointmentOutputsUploading', true)

    for (const file of files) {
      try {
        const uploadUrl = `/admin/appointmentFiles/fileUploadURL/${rootState.adminAppointmentFiles.appointmentId}`
        const attachUrl = `/admin/appointmentFiles/attachOutputs/${rootState.adminAppointmentFiles.appointmentId}`

        if (file.name.length < 100 && file.type) {
          const { signedUrl, external_filename } = await getSignedUrl(
            {
              filename: file.name,
              filetype: file.type,
              folder_name: 'files',
            },
            uploadUrl
          )

          await uploadToS3(signedUrl, file)
          await attachToResource(
            { external_filename, filename: file.name },
            attachUrl
          )
        } else {
          commit('setIsAppointmentOutputsUploading', false)
          dispatch(
            'showErrorMessage',
            Vue.i18n.translate('upload.success.uploaded')
          )

          return
        }
      } catch ({ data }) {
        commit('setIsAppointmentOutputsUploading', false)

        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)
        return
      }
    }

    await dispatch('fetchAppointmentFilesAsAdmin', rootState.adminAppointmentFiles.appointmentId)
    await dispatch('showSuccessMessage', Vue.i18n.translate('upload.success.uploaded'))

    commit('setAdminAppointmentOutputsToDelete', [])
    commit('setIsAppointmentOutputsUploading', false)
  },

  async deleteAppointmentOutputsAsAdmin({ commit, dispatch, rootState }) {
    commit('setIsAppointmentFilesDeleting', true)

    try {``
      await api.post(
        `/admin/appointmentFiles/deleteOutputs/${rootState.adminAppointmentFiles.appointmentId}`,
        {
          file_ids: rootState.adminAppointmentFiles.adminAppointmentOutputsToDelete.map(
            (file) => file.id
          ),
        }
      )

      await dispatch('fetchAppointmentFilesAsAdmin', rootState.adminAppointmentFiles.appointmentId)
      await dispatch('showSuccessMessage', 'Arquivo excluído com sucesso')

      commit('setAdminAppointmentOutputsToDelete', [])
      commit('closeAllModals')
    } catch ({ message }) {
      dispatch('showErrorMessage', message)
    } finally {
      commit('setIsAppointmentFilesDeleting', false)
    }
  },

  async attachAppointmentFilesAsAdmin({ commit, dispatch, rootState }, files) {
    commit('setIsAppointmentFilesUploading', true)

    for (const file of files) {
      try {
        const uploadUrl = `/admin/appointmentFiles/fileUploadURL/${rootState.adminAppointmentFiles.appointmentId}`
        const attachUrl = `/admin/appointmentFiles/attachFile/${rootState.adminAppointmentFiles.appointmentId}`

        if (file.name.length < 100 && file.type) {
          const { signedUrl, external_filename } = await getSignedUrl(
            {
              filename: file.name,
              filetype: file.type,
              folder_name: 'files',
            },
            uploadUrl
          )

          await uploadToS3(signedUrl, file)
          await attachToResource(
            { external_filename, filename: file.name },
            attachUrl
          )
        } else {
          commit('setIsAppointmentFilesUploading', false)
          dispatch('showErrorMessage', Vue.i18n.translate('upload.success.uploaded'))

          return
        }
      } catch ({ data }) {
        commit('setIsAppointmentFilesUploading', false)

        data?.errors && data?.errors?.length >= 1
          ? dispatch('showErrorMessage', data.errors[0].message)
          : dispatch('showErrorMessage', data.message)

        return
      }
    }

    await dispatch('fetchAppointmentFilesAsAdmin', rootState.adminAppointmentFiles.appointmentId)
    await dispatch('showSuccessMessage', Vue.i18n.translate('upload.success.uploaded'))

    commit('setAdminAppointmentFilesToDelete', [])
    commit('setIsAppointmentFilesUploading', false)
  },

  async deleteAppointmentFileAsAdmin({ commit, dispatch, rootState }) {
    commit('setIsAppointmentFilesDeleting', true)

    try {
      await api.post(
        `/admin/appointmentFiles/deleteFiles/${rootState.adminAppointmentFiles.appointmentId}`,
        {
          file_ids: rootState.adminAppointmentFiles.adminAppointmentFilesToDelete.map(
            (file) => file.id
          ),
        }
      )

      await dispatch('fetchAppointmentFilesAsAdmin', rootState.adminAppointmentFiles.appointmentId)
      await dispatch('showSuccessMessage', Vue.i18n.translate('upload.success.excluded'))

      commit('closeAllModals')
      commit('setAdminAppointmentFilesToDelete', [])
    } catch ({ message }) {
      dispatch('showErrorMessage', message)
    } finally {
      commit('setIsAppointmentFilesDeleting', false)
    }
  },
}

const mutations = {
  setAppointmentId: (state, appointmentId) => {
    state.appointmentId = appointmentId
  },
  setAdminAppointmentFiles: (state, adminAppointmentFiles) => {
    state.adminAppointmentFiles = adminAppointmentFiles
  },
  setAdminAppointmentOutputs: (state, adminAppointmentOutputs) => {
    state.adminAppointmentOutputs = adminAppointmentOutputs
  },
  setAdminAppointmentFilesToDelete: (state, adminAppointmentFilesToDelete) => {
    state.adminAppointmentFilesToDelete = adminAppointmentFilesToDelete
  },
  setAdminAppointmentOutputsToDelete: (state, adminAppointmentOutputsToDelete) => {
    state.adminAppointmentOutputsToDelete = adminAppointmentOutputsToDelete
  },
  setIsAppointmentOutputsUploading:(state, status) => {
    state.isAppointmentOutputsUploading = status
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
